import { numfmt } from '@univerjs/core';
import { FORMATS, NUMBER_FMT, DOLLAR_FMT, PERCENT_FMT, US_DOLLAR_FMT, JAPANESE_YEN_FMT, CURRENCY_ROUNDED_FMT, GENERAL_FMT, NON_ABBREVIATABLE_FMTS } from '@/config/spreadsheet';
import { dateStringToDate, dateToExcelNumber } from './date';
import { isDurationString, durationStringToNumber } from './time';
import { stringToNumber, fromExponential, isExponential } from './numbers';
import { trimTrailingDecimals } from './decimals';
export const MAX_DECIMAL_LENGTH = 14; // Discover the most likely active format for a given target

export const findActiveFormat = target => {
  if (!target) return null; // Fix for percentages returning scientific

  if (target.search(/\d\.*\d*%/g) > -1) {
    return PERCENT_FMT;
  } // Fix for generic dollar format


  if (target.search(/"\$"#,##0(\.00)?/g) > -1) {
    return DOLLAR_FMT;
  } // Fix for US Dollar format


  if (target.search(/\[\$\$\]#,##0(\.00)?/g) > -1) {
    return US_DOLLAR_FMT;
  } // Check if an exact match for Japanese Yen and return a Jap format


  if (target === JAPANESE_YEN_FMT.value) {
    return JAPANESE_YEN_FMT;
  }

  if (target === 'Currency Rounded') {
    return CURRENCY_ROUNDED_FMT;
  } // Check if an exact match for Currency Rounded and return a Currency Rounded format


  if (target === CURRENCY_ROUNDED_FMT.value) {
    return CURRENCY_ROUNDED_FMT;
  } // Check if an exact match for General and return a General format


  if (target === GENERAL_FMT.value) {
    return GENERAL_FMT;
  }

  return FORMATS.find(_ref => {
    let {
      value
    } = _ref;
    return isPatternEqualWithoutDecimal(target, value);
  }) || null;
}; // Strip out decimals from pattern and compare

const isPatternEqualWithoutDecimal = (patternA, patternB) => {
  if (patternA && !patternB || !patternA && patternB) {
    return false;
  }

  const partitionAPattern = trimTrailingDecimals(patternA);
  const partitionBPattern = trimTrailingDecimals(patternB);
  return partitionAPattern === partitionBPattern;
}; // decimal just use positive,negative configuration ignored
// Source: https://github.com/dream-num/univer/blob/7d0b87e1dd336216319d19c3f593a890f746a1ba/packages/sheets-numfmt/src/utils/decimal.ts#L19C1-L29C3


export const getDecimalFromPattern = function (pattern) {
  let defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

  if (!pattern) {
    return defaultValue;
  }

  const info = numfmt.getInfo(pattern);
  return Math.min(info.maxDecimals ?? defaultValue, MAX_DECIMAL_LENGTH);
};
export const getDecimalString = length => new Array(Math.min(Math.max(0, Number(length)), 30)).fill(0).join(''); // Extract the numeric portion of a string

export const getNumberFromString = function (value) {
  var _numfmt$parseNumber;

  let defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  const number = (_numfmt$parseNumber = numfmt.parseNumber(`${stringToNumber(value, 0)}`)) === null || _numfmt$parseNumber === void 0 ? void 0 : _numfmt$parseNumber.v;
  if (isNaN(Number(number))) return defaultValue;
  return number;
}; // Source: https://github.com/dream-num/univer/blob/7d0b87e1dd336216319d19c3f593a890f746a1ba/packages/sheets-numfmt/src/utils/decimal.ts#L65
// NOTE: #677 check if exported by '@univerjs/sheets-numfmt'

export const setPatternDecimal = (patterns, decimalLength) => {
  const tokens = patterns.split(';').map(value => {
    // convert to NUMBER_FMT.value if value is non-decimal
    const pattern = !isPatternHasDecimal(value) ? NUMBER_FMT.value : value;

    if (/\.0?/.test(pattern)) {
      return pattern.replace(/\.0*/g, `${decimalLength > 0 ? '.' : ''}${getDecimalString(Number(decimalLength || 0))}`);
    }

    if (/0([^0]?)|0$/.test(pattern)) {
      return pattern.replace(/0([^0]+)|0$/, `0${decimalLength > 0 ? '.' : ''}${getDecimalString(Number(decimalLength || 0))}$1`);
    }

    return pattern;
  });
  return tokens.join(';');
}; // Source: https://github.com/dream-num/univer/blob/7d0b87e1dd336216319d19c3f593a890f746a1ba/packages/sheets-numfmt/src/utils/decimal.ts#L85
// NOTE: #677 check if exported by '@univerjs/sheets-numfmt'

export const isPatternHasDecimal = pattern => /\.0?/.test(pattern) || /0([^0]?)|0$/.test(pattern); // check if foramt is non-abbreviatable

export const isFormatNonAbbreviatable = format => {
  return Boolean(NON_ABBREVIATABLE_FMTS.find(fmt => fmt.value === format));
}; // Attempt to convert an excel formatted string
// into the best approximation of the original number
// NOTE: returns NaN if unsuccessful

export const unformatNumber = input => {
  if (!input) return NaN; // Handle percentage format

  if (input.search(/%$/) > -1) {
    return parseFloat(input.replace(/%$/, '')) / 100;
  } // Reverses scientific notation


  if (isExponential(input)) {
    return Number(fromExponential(input));
  } // If parseable into a time or duration,
  // convert to Excel number


  if (isDurationString(input)) {
    return durationStringToNumber(input);
  }

  const date = dateStringToDate(input); // If parseable into a date,
  // convert to Excel number

  if (date) {
    return dateToExcelNumber(date);
  } // Remove currency symbols, spaces, and commas


  const cleaned = input.replace(/\(([^)]+)\)/g, '-$1'); // convert (100) to -100 for negatives

  return Number(cleaned.replace(/[^0-9.-]+/g, ''));
};